import { formatDate, formatTime } from "@/shared/lib/date-time";
import {
  formatAssistInstructorBillLine,
} from "@/shared/lib/receipt-print-48mm";
import type { ReceiptItemDTO } from "@/modules/billing/presentation/api/client";

export type RoomBookingReceiptGroup = {
  roomItem: ReceiptItemDTO;
  assistItems: ReceiptItemDTO[];
};

export function groupRoomBookingReceiptItems(
  items: ReceiptItemDTO[],
): {
  roomBookingGroups: RoomBookingReceiptGroup[];
  orphanAssistItems: ReceiptItemDTO[];
  otherItems: ReceiptItemDTO[];
} {
  const roomItems = items.filter((i) => i.itemType === "room_booking");
  const assistItems = items.filter((i) => i.itemType === "room_booking_assist");
  const otherItems = items.filter(
    (i) =>
      i.itemType !== "room_booking" && i.itemType !== "room_booking_assist",
  );

  const matchedAssistIds = new Set<string>();
  const roomBookingGroups: RoomBookingReceiptGroup[] = roomItems.map(
    (roomItem) => {
      const related = assistItems.filter((a) => {
        if (a.roomBookingId !== roomItem.roomBookingId) return false;
        matchedAssistIds.add(a.id);
        return true;
      });
      return { roomItem, assistItems: related };
    },
  );

  const orphanAssistItems = assistItems.filter((a) => !matchedAssistIds.has(a.id));

  return { roomBookingGroups, orphanAssistItems, otherItems };
}

export function hasPricedAssistReceiptItems(items: ReceiptItemDTO[]): boolean {
  return items.some((i) => i.itemType === "room_booking_assist");
}

type RoomBookingReceiptGroupCardProps = {
  group: RoomBookingReceiptGroup;
  showLegacyAssistNames?: boolean;
};

/** การ์ดรวม: ค่าจองห้อง + ค่าครูช่วย (ตามรูปแบบบิล) */
export function RoomBookingReceiptGroupCard({
  group,
  showLegacyAssistNames = true,
}: RoomBookingReceiptGroupCardProps) {
  const { roomItem, assistItems } = group;
  const legacyAssistLine =
    showLegacyAssistNames && assistItems.length === 0
      ? formatAssistInstructorBillLine(roomItem.assistInstructorNames)
      : null;

  return (
    <div className="rounded-lg border p-4">
      <p className="whitespace-pre-line font-medium">{roomItem.description}</p>

      <div className="mt-2 space-y-1 text-muted-foreground text-sm">
        {!roomItem.description.includes("\n") &&
          roomItem.roomName &&
          roomItem.date && (
            <>
              <p>ຫ້ອງ: {roomItem.roomName}</p>
              <p>
                ວັນທີ: {formatDate(roomItem.date)}
                {roomItem.startTime &&
                  roomItem.endTime &&
                  ` · ເວລາ: ${formatTime(roomItem.startTime)} – ${formatTime(roomItem.endTime)}`}
              </p>
            </>
          )}

        <div className="space-y-2 border-t pt-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-medium text-foreground">ຈອງຫ້ອງ</p>
              <p>
                {roomItem.quantity} ×{" "}
                {roomItem.unitPrice.toLocaleString("lo-LA")} ກີບ
              </p>
            </div>
            <p className="shrink-0 font-semibold text-foreground">
              {roomItem.totalPrice.toLocaleString("lo-LA")} ກີບ
            </p>
          </div>

          {assistItems.map((assist) => (
            <div
              key={assist.id}
              className="flex items-start justify-between gap-3 border-t border-dashed pt-2"
            >
              <div>
                <p className="font-medium text-foreground">
                  {assist.description}
                </p>
                <p>
                  {assist.quantity} ×{" "}
                  {assist.unitPrice.toLocaleString("lo-LA")} ກີບ
                </p>
              </div>
              <p className="shrink-0 font-semibold text-foreground">
                {assist.totalPrice.toLocaleString("lo-LA")} ກີບ
              </p>
            </div>
          ))}

          {legacyAssistLine ? <p>{legacyAssistLine}</p> : null}
        </div>
      </div>

      <div className="mt-3 flex justify-end border-t pt-3">
        <p className="font-semibold">
          {(roomItem.totalPrice +
            assistItems.reduce((s, a) => s + a.totalPrice, 0)
          ).toLocaleString("lo-LA")}{" "}
          ກີບ
        </p>
      </div>
    </div>
  );
}

export function renderStandaloneReceiptItemLines(item: ReceiptItemDTO) {
  return {
    title: item.description,
    quantityLine: `${item.quantity} × ${item.unitPrice.toLocaleString("lo-LA")} ກີບ`,
    total: `${item.totalPrice.toLocaleString("lo-LA")} ກີບ`,
  };
}
